import { useEffect, useRef, useState } from 'react';

export interface MultiSelectOption {
  value: string;
  label: string;
}

/**
 * A dropdown that selects SEVERAL values - a checkbox list in a popover,
 * where a native <select multiple> would be unusable. Built for filter
 * bars: the trigger reads like the neighbouring selects, summarises the
 * selection ("Ranjith +2"), and a Clear row empties it in one click.
 */
export function MultiSelect({
  placeholder,
  options,
  selected,
  onChange,
  widthClass = 'w-44',
  compact = false,
}: {
  /** Trigger text while nothing is selected, e.g. "Any assignee". */
  placeholder: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  widthClass?: string;
  /** Smaller trigger, for dense header bars (matches text-xs selects). */
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Click-away and Escape both close - standard popover manners.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function toggle(value: string) {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );
  }

  const firstLabel = options.find((o) => o.value === selected[0])?.label ?? '';
  const summary =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? firstLabel
        : `${firstLabel} +${selected.length - 1}`;

  return (
    <div ref={rootRef} className={`relative ${widthClass}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={[
          'flex w-full items-center justify-between gap-2 rounded-lg border bg-white text-left outline-none transition',
          compact ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm',
          selected.length > 0
            ? 'border-brand-500 text-slate-900 ring-2 ring-brand-100'
            : 'border-slate-300 text-slate-700 hover:bg-slate-50',
        ].join(' ')}
      >
        <span className="truncate">{summary}</span>
        <span className="shrink-0 text-[10px] text-slate-400">▼</span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          className="absolute left-0 right-0 top-full z-30 mt-1 max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-xl ring-1 ring-black/5"
        >
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="block w-full px-3 py-1.5 text-left text-xs font-medium text-brand-600 transition hover:bg-slate-50"
            >
              Clear selection
            </button>
          )}
          {options.length === 0 && (
            <p className="px-3 py-2 text-xs text-slate-400">Nothing to choose from.</p>
          )}
          {options.map((option) => {
            const checked = selected.includes(option.value);
            return (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-2.5 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(option.value)}
                  className="h-4 w-4 rounded border-slate-300 accent-brand-600"
                />
                <span className={`truncate ${checked ? 'font-medium text-slate-900' : ''}`}>
                  {option.label}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
